import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Upload, Download, Info } from 'lucide-react';

import useMealMenuApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import { importMealMenus, downloadMealMenuTemplate } from '../../services/mealMenuService';
import { getMeals } from '../../services/mealService';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const ImportBar = styled.div`
  display: flex; justify-content: flex-end; gap: 12px;
  margin-bottom: 12px;
`;

const InfoBanner = styled.div`
  display: flex; align-items: flex-start; gap: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #1e40af;
  line-height: 1.5;
`;

const MealChips = styled.span`
  font-weight: 600;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ResultOverlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000;
`;

const ResultModal = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  width: 480px; max-width: 90vw;
  max-height: 80vh; overflow-y: auto;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const ResultTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 12px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 14px;
`;

const StatCard = styled.div`
  background: ${({ $tone }) =>
    $tone === 'ok' ? '#ecfdf5' :
    $tone === 'warn' ? '#fff7ed' :
    $tone === 'err' ? '#fef2f2' :
    '#f8fafc'};
  border-radius: 4px;
  padding: 10px;
  text-align: center;
`;

const StatLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px; color: #64748b;
`;

const StatValue = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-top: 4px;
`;

const ErrorList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #fee2e2;
  border-radius: 4px;
  background: #fef2f2;
  padding: 8px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #991b1b;
`;

const ErrorRow = styled.div`
  padding: 4px 0;
  border-bottom: 1px solid #fecaca;
  &:last-child { border-bottom: none; }
`;

const ModalActions = styled.div`
  display: flex; justify-content: flex-end; margin-top: 14px;
`;

export default function MealMenusPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 20, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [mealNames, setMealNames] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!canWrite('CN')) return;
    (async () => {
      try {
        const res = await getMeals({ pageSize: 100, pageNumber: 1 });
        const list = res.data?.data?.data || [];
        const names = [...list]
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((m) => m.name);
        setMealNames(names);
      } catch { /* interceptor */ }
    })();
  }, [canWrite]);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete, refresh } = useMealMenuApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    const items = Array.isArray(row.items) ? row.items.join(', ') : (row.items || '');
    setSelectedRow({ ...row, items });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: `${row.menuDate} — ${row.mealName}` });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('canteen.menus').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const res = await downloadMealMenuTemplate();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'meal-menu-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* interceptor */ }
  }, []);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChosen = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImporting(true);
    try {
      const res = await importMealMenus(file);
      setImportResult(res.data?.data || null);
      refresh?.();
    } catch { /* interceptor */ }
    setImporting(false);
  }, [refresh]);

  const closeResult = useCallback(() => setImportResult(null), []);

  const actionOptions = useMemo(() => {
    if (!canWrite('CN')) return [];
    return [
      { type: 'edit', label: t('canteen.editMenu'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('common.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('canteen.editMenu') : t('canteen.newMenu')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('common.delete')}
        message={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {canWrite('CN') && (
        <>
          {mealNames.length > 0 ? (
            <InfoBanner>
              <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>
                {t('canteen.importHintAvailableMeals')}{' '}
                <MealChips>{mealNames.join(', ')}</MealChips>.{' '}
                {t('canteen.importHintDropdown')}
              </span>
            </InfoBanner>
          ) : (
            <InfoBanner>
              <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{t('canteen.importHintNoMeals')}</span>
            </InfoBanner>
          )}
          <ImportBar>
            <Button variant="ghost" onClick={handleDownloadTemplate}>
              <Download size={14} /> {t('canteen.downloadTemplate')}
            </Button>
            <Button variant="ghost" onClick={handleImportClick} disabled={importing || mealNames.length === 0}>
              <Upload size={14} /> {importing ? '...' : t('canteen.importMenu')}
            </Button>
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileChosen}
            />
          </ImportBar>
        </>
      )}

      {importResult && (
        <ResultOverlay onClick={closeResult}>
          <ResultModal onClick={(e) => e.stopPropagation()}>
            <ResultTitle>{t('canteen.importMenu')}</ResultTitle>
            <StatsRow>
              <StatCard>
                <StatLabel>{t('common.totalRows', { defaultValue: 'Satır' })}</StatLabel>
                <StatValue>{importResult.totalRows ?? 0}</StatValue>
              </StatCard>
              <StatCard $tone="ok">
                <StatLabel>{t('common.created', { defaultValue: 'Eklendi' })}</StatLabel>
                <StatValue>{importResult.created ?? 0}</StatValue>
              </StatCard>
              <StatCard $tone="warn">
                <StatLabel>{t('common.updated', { defaultValue: 'Güncellendi' })}</StatLabel>
                <StatValue>{importResult.updated ?? 0}</StatValue>
              </StatCard>
              <StatCard $tone="err">
                <StatLabel>{t('common.skipped', { defaultValue: 'Atlandı' })}</StatLabel>
                <StatValue>{importResult.skipped ?? 0}</StatValue>
              </StatCard>
            </StatsRow>
            {importResult.errors?.length > 0 && (
              <ErrorList>
                {importResult.errors.map((err, i) => (
                  <ErrorRow key={i}>
                    #{err.rowNumber}: {t(`responseErrors.${err.errorKey}`, { defaultValue: err.errorKey })}
                    {err.details ? ` — ${err.details}` : ''}
                  </ErrorRow>
                ))}
              </ErrorList>
            )}
            <ModalActions>
              <Button onClick={closeResult}>{t('common.close', { defaultValue: 'Kapat' })}</Button>
            </ModalActions>
          </ResultModal>
        </ResultOverlay>
      )}

      <DataTable
        title={t('canteen.menus')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('CN')}
        onNewButtonClick={handleNewButtonClick}
        onRowsDelete={canWrite('CN') ? handleRowsDelete : undefined}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
      />
    </Container>
  );
}
